/**
 * ReasonDialog — a modal that captures a mandatory written reason (motiu) before
 * a state change. Used for suspend / reactivate / soft-delete, where CLAUDE.md
 * requires a documented reason. Token-driven; all copy supplied by the caller.
 */

import React from 'react';
import { Button, useTheme } from '@obrava/ui';
import { useTranslation } from 'react-i18next';
import { Banner, type BannerTone } from './Banner.js';
import { Textarea } from './Textarea.js';

export interface ReasonDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  /** Tone of the explanatory banner (e.g. danger for soft-delete). */
  readonly tone: BannerTone;
  /** Label of the confirm CTA. */
  readonly confirmLabel: string;
  readonly confirmDisabled?: boolean;
  /** Called with the trimmed reason once it is non-empty. */
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

export function ReasonDialog(props: ReasonDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const theme = useTheme();
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  // Reset the field whenever the dialog re-opens.
  React.useEffect(() => {
    if (props.open) {
      setReason('');
      setTouched(false);
    }
  }, [props.open]);

  if (!props.open) return null;

  const trimmed = reason.trim();
  const missing = trimmed.length === 0;

  const onConfirm = (): void => {
    setTouched(true);
    if (missing) return;
    props.onConfirm(trimmed);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.color.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing.space[6],
        zIndex: 100,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: theme.spacing.size.dialogMaxWidth,
          backgroundColor: theme.color.surfaceOverlay,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radii.radius.card,
          boxShadow: theme.radii.shadow.overlay,
          padding: theme.spacing.space[7],
        }}
      >
        <h2
          style={{
            margin: `0 0 ${theme.spacing.space[3]}px`,
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.title3,
            fontWeight: theme.typography.weight.semibold,
            color: theme.color.textPrimary,
          }}
        >
          {props.title}
        </h2>

        <Banner tone={props.tone} style={{ marginBottom: theme.spacing.space[5] }}>
          {props.body}
        </Banner>

        <Textarea
          label={t('platformConsole.detail:reasonLabel')}
          placeholder={t('platformConsole.detail:reasonPlaceholder')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={touched && missing ? t('platformConsole.detail:reasonRequired') : undefined}
          rows={3}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing.space[4],
            marginTop: theme.spacing.space[6],
          }}
        >
          <Button variant="secondary" onClick={props.onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={props.confirmDisabled}>
            {props.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
